import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { map, catchError, throwError } from 'rxjs';
import { isTrustedApiRequest } from '../trusted-api-request.util';
import {
  DomainError,
  NotFoundError,
  TenantForbiddenError,
  AuthenticationError,
  ValidationError,
  TenantServerError,
} from '../errors/domain-errors';

/**
 * Response envelope interceptor
 *
 * Unwraps the standard backend API envelope:
 *   { "data": {...}, "meta": { "requestId": "...", ... }, "errors": [] }
 *
 * - If body has a non-empty `errors` array → maps first error to a DomainError and throws
 * - If body has a `data` key → replaces body with body.data
 * - Otherwise (non-envelope, blobs, auth) → passes through unchanged
 *
 * Only applies to trusted API requests. Does not modify auth/external URLs.
 */
export const responseEnvelopeInterceptor: HttpInterceptorFn = (req, next) => {
  const environment = inject<{ apiBaseUrl?: string }>('ENVIRONMENT' as never);

  if (!isTrustedApiRequest(req.url, environment.apiBaseUrl)) {
    return next(req);
  }

  return next(req).pipe(
    map(event => {
      if (!(event instanceof HttpResponse)) {
        return event;
      }

      const body = event.body;

      if (!isEnvelopeBody(body)) {
        return event;
      }

      // Non-empty errors array → throw mapped DomainError
      if (Array.isArray(body['errors']) && body['errors'].length > 0) {
        throw mapBackendError(body['errors'][0]);
      }

      // Unwrap data
      if ('data' in body) {
        return event.clone({ body: body['data'] });
      }

      return event;
    }),
    catchError(err => {
      // Re-throw DomainErrors as-is; wrap anything else
      if (err instanceof DomainError) {
        return throwError(() => err);
      }
      return throwError(() => err);
    })
  );
};

function isEnvelopeBody(body: unknown): body is Record<string, unknown> {
  return body !== null && typeof body === 'object' && !Array.isArray(body) &&
    ('data' in (body as object) || 'errors' in (body as object) || 'meta' in (body as object));
}

function mapBackendError(error: unknown): DomainError {
  if (!error || typeof error !== 'object') {
    return new DomainError('Unknown server error', 'UNKNOWN_ERROR', 'server');
  }

  const err = error as { code?: string; message?: string };
  const code = typeof err.code === 'string' ? err.code : 'UNKNOWN_ERROR';
  // Sanitize backend message: truncate and strip angle-bracket tags to prevent XSS
  // if a future code path renders error messages without escaping.
  // Backend messages are designed to be user-safe (api-schema-strategy §4.9) but
  // defensive sanitization here reduces risk regardless of backend behaviour.
  const rawMessage = typeof err.message === 'string' ? err.message : 'An error occurred';
  const message = rawMessage.replace(/<[^>]*>/g, '').slice(0, 200);

  switch (code) {
    case 'RESOURCE_NOT_FOUND':
    case 'NOT_FOUND':
      return new NotFoundError(message);
    case 'FORBIDDEN':
    case 'TENANT_FORBIDDEN':
      return new TenantForbiddenError(message);
    case 'UNAUTHORIZED':
    case 'AUTHENTICATION_REQUIRED':
      return new AuthenticationError(message);
    case 'VALIDATION_ERROR':
      return new ValidationError(message);
    case 'SERVER_ERROR':
    case 'INTERNAL_SERVER_ERROR':
      return new TenantServerError(message);
    default:
      return new DomainError(message, code, 'server', undefined, true);
  }
}
